import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';

// パスワード表示切替の目アイコン【2026-07-22 追加】
const EyeIcon = ({ off }) => off ? (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="2" y1="2" x2="22" y2="22"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/></svg>
) : (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
);

// サーバ側と同じルール（8〜128文字）でリアルタイム検証【2026-07-22 追加】
const passwordError = (pw) => {
  if (!pw) return '';
  if (pw.length < 8)   return `使用不可: パスワードは8文字以上にしてください（あと${8 - pw.length}文字）`;
  if (pw.length > 128) return '使用不可: パスワードは128文字以内にしてください';
  return '';
};

/**
 * 教員登録ページ（招待 URL から到達）
 *   URL 例: https://app.nekotam.com/signup-teacher?code=abc123def456...  （2026-09-04 ドメイン分離）
 *
 * クエリ ?code= を読み取り、内部状態に保持して
 * /api/auth/signup-teacher へ POST する。
 */
export default function SignupTeacher({ onLogin }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const codeFromUrl = searchParams.get('code') || '';

  const [form, setForm] = useState({
    student_id: '',
    name:       '',
    email:      '',
    password:   '',
    inviteCode: codeFromUrl
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [done,    setDone]    = useState(false);
  // 利用規約・プライバシーポリシーへの同意【2026-06-05 追加】
  const [agreed,  setAgreed]  = useState(false);
  // パスワード表示切替【2026-07-22 追加】
  const [showPw, setShowPw] = useState(false);
  const pwError = passwordError(form.password);

  // URL に ?code= が無い場合の警告
  const codeMissing = !codeFromUrl;

  useEffect(() => {
    if (codeFromUrl) setForm(p => ({ ...p, inviteCode: codeFromUrl }));
  }, [codeFromUrl]);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (!form.inviteCode) {
      setError('招待コードがありません。管理者から発行された URL から開きなおしてください。');
      return;
    }
    if (form.password.length < 8) {
      setError('パスワードは8文字以上にしてください');
      return;
    }
    if (!agreed) {
      setError('利用規約とプライバシーポリシーに同意してください');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/signup-teacher', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          studentId:  form.student_id,
          name:       form.name,
          email:      form.email,
          password:   form.password,
          inviteCode: form.inviteCode
        })
      });
      const data = await res.json();
      if (data.success) {
        // 自動ログイン
        if (typeof onLogin === 'function') {
          onLogin(data.user, data.token);
        } else {
          // 万一 onLogin が無くてもトークンを保存して /login へ
          localStorage.setItem('token', data.token);
        }
        setDone(true);
        setTimeout(() => navigate('/'), 1500);
      } else {
        setError(data.error || data.message || '登録に失敗しました');
      }
    } catch {
      setError('ネットワークエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // ----- スタイル（Login.jsx と同じテイスト） ------------------------------
  const s = {
    wrap: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
      padding: '1rem'
    },
    card: {
      background: '#fff',
      borderRadius: '16px',
      padding: '2.5rem 2rem',
      width: '100%',
      maxWidth: '440px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
    },
    logo: { textAlign: 'center', marginBottom: '1.5rem' },
    logoIcon: { fontSize: '3rem', display: 'block', marginBottom: '0.5rem' },
    logoTitle: { fontSize: '1.4rem', fontWeight: 700, color: '#1e3a8a', margin: 0 },
    logoSub: { fontSize: '0.85rem', color: '#888', margin: '0.25rem 0 0 0' },
    badge: {
      background: '#3b82f622',
      color: '#3b82f6',
      borderRadius: '999px',
      padding: '0.25rem 0.75rem',
      fontSize: '0.8rem',
      fontWeight: 600,
      display: 'inline-block',
      marginBottom: '1rem'
    },
    field: { marginBottom: '1rem' },
    label: { display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#555', marginBottom: '0.35rem' },
    input: {
      width: '100%',
      padding: '0.65rem 0.9rem',
      border: '1.5px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '1rem',
      boxSizing: 'border-box',
      outline: 'none'
    },
    inputDisabled: {
      background: '#f3f4f6',
      color: '#666',
      cursor: 'not-allowed'
    },
    error: {
      background: '#fdecea',
      color: '#c62828',
      border: '1px solid #ef9a9a',
      borderRadius: '8px',
      padding: '0.65rem 0.9rem',
      marginBottom: '1rem',
      fontSize: '0.9rem'
    },
    warn: {
      background: '#fff7ed',
      color: '#9a3412',
      border: '1px solid #fdba74',
      borderRadius: '8px',
      padding: '0.65rem 0.9rem',
      marginBottom: '1rem',
      fontSize: '0.9rem'
    },
    success: {
      background: '#ecfdf5',
      color: '#166534',
      border: '1px solid #6ee7b7',
      borderRadius: '8px',
      padding: '0.65rem 0.9rem',
      marginBottom: '1rem',
      fontSize: '0.9rem'
    },
    btn: (disabled) => ({
      width: '100%',
      padding: '0.8rem',
      background: disabled ? '#93c5fd' : '#2563eb',
      color: '#fff',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: 700,
      cursor: disabled ? 'not-allowed' : 'pointer',
      marginTop: '0.5rem'
    }),
    footer: { textAlign: 'center', fontSize: '0.85rem', color: '#888', marginTop: '1rem' }
  };

  // ----- 招待コードの先頭 8 文字だけ表示 ------------------------------------
  const codePreview = form.inviteCode
    ? form.inviteCode.slice(0, 8) + '...' + form.inviteCode.slice(-4)
    : '';

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.logo}>
          <span style={{ ...s.logoIcon, color: '#1e3a8a', display: 'flex', justifyContent: 'center' }}>
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 9.5 12 4 2 9.5l10 5.5z"/><path d="M6 12.5V17c0 1.66 2.69 3 6 3s6-1.34 6-3v-4.5"/><path d="M22 9.5V15"/></svg>
          </span>
          <p style={s.logoTitle}>教員アカウント登録</p>
          <p style={s.logoSub}>Teacher Sign-up</p>
        </div>

        <span style={s.badge}>招待コード: {codePreview || '未設定'}</span>

        {codeMissing && (
          <div style={s.warn}>
            招待コードが URL に含まれていません。<br/>
            管理者から発行された招待 URL から開きなおしてください。
          </div>
        )}

        {done && (
          <div style={s.success}>
            登録完了！ダッシュボードに移動します...
          </div>
        )}

        {error && <div style={s.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={s.field}>
            <label style={s.label}>氏名</label>
            <input style={s.input} name="name" value={form.name} onChange={handleChange}
              placeholder="山田 太郎" required />
          </div>

          <div style={s.field}>
            <label style={s.label}>学籍番号 / 教員 ID</label>
            <input style={s.input} name="student_id" value={form.student_id} onChange={handleChange}
              placeholder="T2025001" required />
          </div>

          <div style={s.field}>
            <label style={s.label}>メールアドレス</label>
            <input style={s.input} name="email" type="email" value={form.email} onChange={handleChange}
              placeholder="yamada@nekotam.com" />
          </div>

          <div style={s.field}>
            <label style={s.label}>パスワード（8文字以上）</label>
            <div style={{ position: 'relative' }}>
              <input style={{ ...s.input, paddingRight: '2.8rem', ...(pwError ? { borderColor: '#dc2626' } : {}) }}
                name="password" type={showPw ? 'text' : 'password'} value={form.password} onChange={handleChange}
                placeholder="••••••••" required minLength={8} />
              <button type="button" onClick={() => setShowPw(v => !v)}
                aria-label={showPw ? 'パスワードを隠す' : 'パスワードを表示'}
                style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
                         background: 'none', border: 'none', padding: '0.25rem', cursor: 'pointer',
                         color: '#888', display: 'flex', alignItems: 'center' }}>
                <EyeIcon off={showPw} />
              </button>
            </div>
            {/* 一文字打つごとに使用不可理由を赤文字表示【2026-07-22 追加】 */}
            {pwError && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.35rem 0 0 0' }}>{pwError}</p>}
          </div>

          {/* 規約同意チェック【2026-06-05 追加】 */}
          <label style={{
            display:      'flex',
            alignItems:   'flex-start',
            gap:          '0.5rem',
            marginBottom: '0.5rem',
            fontSize:     '0.85rem',
            color:        '#374151',
            cursor:       'pointer',
            lineHeight:   1.5
          }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              style={{ marginTop: '0.2rem', width: '16px', height: '16px', flexShrink: 0 }}
            />
            <span>
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>利用規約</a>
              および
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>プライバシーポリシー</a>
              に同意します
            </span>
          </label>

          {/* 未同意・コード無しでも押せるようにし、handleSubmit 側でエラー表示する（disabled だと無反応で分かりにくいため）【2026-07-22】 */}
          <button type="submit" style={s.btn(loading || done)} disabled={loading || done}>
            {loading ? '登録中...' : done ? '完了' : '教員アカウントを作成'}
          </button>
        </form>

        <div style={s.footer}>
          すでにアカウントをお持ちの方は <Link to="/login" style={{ color: '#2563eb' }}>ログイン</Link>
        </div>
      </div>
    </div>
  );
}
